/**
 * <review-highlights>
 * Optionally hydrates the review cards from a JSON endpoint that returns
 * Yotpo's shape: { "reviews": [ { score, content, name, reviewer_type }, ... ] }.
 *
 * Security: the endpoint must NOT require the secret X-Yotpo-Token (theme code
 * is public). Point data-endpoint at a Shopify App Proxy path or a public
 * endpoint that already proxies Yotpo server-side.
 *
 * If no endpoint is set, the fetch fails, or it returns nothing, the manually
 * authored review blocks rendered by Liquid remain untouched (graceful
 * fallback). Scoped to this section; script loads only when the section renders.
 *
 * "Load more": the existing view-more button reveals one extra batch of
 * reviews (BATCH_SIZE) fetched alongside the initial set, then hides itself.
 * Only the newly requested cards are rendered (existing cards are untouched),
 * and cards are sliced by a running pointer so none are ever duplicated. The
 * appended <li>s reuse the same card markup, so on desktop they flow into new
 * rows and on mobile they become additional slides in the same carousel.
 */
if (!customElements.get('review-highlights')) {
  customElements.define(
    'review-highlights',
    class ReviewHighlights extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        this.grid = this.querySelector('[data-reviews-grid]');
        if (!this.endpoint || !this.grid) return;

        this.limit = parseInt(this.dataset.limit, 10) || 3;
        this.batchSize = 6; // reviews revealed per "Load more" click
        this.verifiedLabel = this.dataset.verifiedLabel || 'Verified Buyer';
        this.rendered = 0;
        this.load();
      }

      /**
       * Request the initial set plus one batch in a single fetch, so a "Load
       * more" click can reveal the extra reviews without another round-trip.
       */
      fetchUrl() {
        try {
          const url = new URL(this.endpoint, window.location.origin);
          url.searchParams.set('per_page', String(this.limit + this.batchSize));
          return url.toString();
        } catch (err) {
          return this.endpoint;
        }
      }

      async load() {
        try {
          const res = await fetch(this.fetchUrl(), { headers: { Accept: 'application/json' } });
          if (!res.ok) return;

          const data = await res.json();
          // Supports both the authenticated /v1/apps shape ({ reviews: [] })
          // and the public widget shape ({ response: { reviews: [] } }).
          const reviews = (data && data.reviews) || (data && data.response && data.response.reviews) || [];
          if (!reviews.length) return;

          this.allReviews = reviews;

          // Replace the manual fallback cards with the initial live set.
          this.grid.innerHTML = '';
          this.appendReviews(this.limit);

          this.setupLoadMore();
        } catch (err) {
          // Network/CORS/parse error — keep the manual Liquid fallback.
        }
      }

      /** Append the next `count` reviews (only the new cards are rendered). */
      appendReviews(count) {
        const next = this.allReviews.slice(this.rendered, this.rendered + count);
        if (!next.length) return 0;
        this.grid.insertAdjacentHTML('beforeend', next.map((r) => this.cardHTML(r)).join(''));
        this.rendered += next.length;
        return next.length;
      }

      /** Wire the existing view-more button to reveal one batch, then hide. */
      setupLoadMore() {
        this.viewMore = this.querySelector('.review-highlights__view-more');
        if (!this.viewMore) return;

        // Nothing more to reveal — hide the button up front.
        if (this.rendered >= this.allReviews.length) {
          this.hideLoadMore();
          return;
        }

        this.onLoadMore = (event) => {
          event.preventDefault();
          this.appendReviews(this.batchSize);
          this.hideLoadMore();
        };
        this.viewMore.addEventListener('click', this.onLoadMore);
      }

      hideLoadMore() {
        if (!this.viewMore) return;
        this.viewMore.removeEventListener('click', this.onLoadMore);
        const actions = this.viewMore.closest('.review-highlights__actions');
        (actions || this.viewMore).style.display = 'none';
      }

      cardHTML(review) {
        const score = Math.max(0, Math.min(5, Math.round(review.score || 0)));
        // verified_buyer (widget API) OR reviewer_type (apps API)
        const verified = review.verified_buyer === true || review.reviewer_type === 'verified_buyer';
        // user.display_name (widget API) OR name (apps API)
        const rawName = (review.user && review.user.display_name) || review.name || '';
        const name = this.escape(rawName);
        const content = this.escape(review.content || '');
        const nameLine = verified ? `${name} (${this.escape(this.verifiedLabel)})` : name;

        return `
          <li class="review-highlights__card" role="listitem">
            <div class="review-highlights__stars" role="img" aria-label="Rated ${score} out of 5">
              ${this.starsHTML(score)}
            </div>
            <blockquote class="review-highlights__quote"><p>${content}</p></blockquote>
            <div class="review-highlights__author">
              ${verified ? this.verifiedIcon() : ''}
              <span class="review-highlights__author-name">${nameLine}</span>
            </div>
          </li>`;
      }

      starsHTML(score) {
        let out = '';
        for (let i = 1; i <= 5; i += 1) {
          out += `<span class="review-highlights__star${i <= score ? ' is-filled' : ''}">&#9733;</span>`;
        }
        return out;
      }

      verifiedIcon() {
        return (
          '<span class="review-highlights__verified-icon" aria-hidden="true">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">' +
          '<path d="M4.21875 6.90813L5.75 9.08376C5.80817 9.1704 5.88617 9.24192 5.97752 9.29238C6.06886 9.34285 6.17093 9.3708 6.27524 9.37391C6.37956 9.37703 6.48311 9.35522 6.57731 9.3103C6.67151 9.26538 6.75363 9.19865 6.81687 9.11563L11.7188 2.91125" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<path d="M1.875 0.46814C1.62636 0.46814 1.3879 0.566912 1.21209 0.742727C1.03627 0.918542 0.9375 1.157 0.9375 1.40564V7.03064C0.9375 10.0388 5.45875 13.3863 6.99562 14.3806C7.14564 14.4785 7.32089 14.5306 7.5 14.5306C7.67911 14.5306 7.85436 14.4785 8.00438 14.3806C9.54125 13.3863 14.0625 10.0388 14.0625 7.03064V1.40564C14.0625 1.157 13.9637 0.918542 13.7879 0.742727C13.6121 0.566912 13.3736 0.46814 13.125 0.46814H1.875Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg></span>'
        );
      }

      /** Escape untrusted API text before inserting into the DOM. */
      escape(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
